import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { api } from '../../lib/api';
import { formatDate, formatEuro } from '../../lib/format';
import { Button } from '../ui/Button';
import { EmptyState } from '../ui/EmptyState';
import { StatutAvoirBadge, TypeAvoirBadge } from './StatutAvoirBadge';

interface Props {
  venteId: number;
}

export function AvoirsTab({ venteId }: Props): JSX.Element {
  const { data, isLoading } = useQuery({
    queryKey: ['avoirs', { venteId }],
    queryFn: () => api.avoirs.list({ vente_id: venteId }),
  });

  if (isLoading) return <p className="text-sm text-slate-500">Chargement…</p>;

  const items = data?.items ?? [];

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-semibold text-slate-200">Avoirs</h3>
        <Link to={`/ventes/${String(venteId)}/avoirs/nouveau`}>
          <Button size="sm">Créer un avoir</Button>
        </Link>
      </div>

      {items.length === 0 ? (
        <EmptyState title="Aucun avoir" description="Aucun avoir n'a été émis pour cette vente." />
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="border-b border-slate-800">
              <tr className="text-left text-xs text-slate-500 uppercase tracking-wider">
                <th className="py-2 pr-3">Date</th>
                <th className="py-2 pr-3">N° Henrri</th>
                <th className="py-2 pr-3">Type</th>
                <th className="py-2 pr-3">Statut</th>
                <th className="py-2 pr-3 text-right">HT</th>
                <th className="py-2 pr-3 text-right">TVA</th>
                <th className="py-2 pr-3 text-right">TTC</th>
              </tr>
            </thead>
            <tbody>
              {items.map((a) => (
                <tr key={a.id} className="border-b border-slate-800/50">
                  <td className="py-2 pr-3">{formatDate(a.date_emission)}</td>
                  <td className="py-2 pr-3">{a.numero_avoir_henrri ?? '—'}</td>
                  <td className="py-2 pr-3">
                    <TypeAvoirBadge type={a.type_avoir} />
                  </td>
                  <td className="py-2 pr-3">
                    <StatutAvoirBadge statut={a.statut} />
                  </td>
                  <td className="py-2 pr-3 text-right">{formatEuro(a.montant_ht_centimes)}</td>
                  <td className="py-2 pr-3 text-right">{formatEuro(a.montant_tva_centimes)}</td>
                  <td className="py-2 pr-3 text-right">{formatEuro(a.montant_ttc_centimes)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
